<template>
	<div>
		<section v-if="invalidCode" class="social-login__guts">
			<c-callout v-if="!showResetLinkSent" type="error">
				<p>Reset link invalid.</p>
			</c-callout>

			<c-callout v-else id="reset-sent" type="info">
				<p>We're emailing you a password reset link now.</p>
			</c-callout>

			<c-button title="Get a new reset password link" class="button button--primary text-4 mb-4" @click="handleForgotPassword">Send a new reset link</c-button>
		</section>

		<section v-else class="social-login__guts">
			<ValidationObserver ref="observer" v-slot="{ invalid, validated }" tag="form" novalidate class="social-login__form" @submit.prevent="handleResetPassword">
				<c-create-password-input v-model="password" placeholder-text="New Password" auto-focus />

				<slot name="captcha" />

				<c-status-button
					class="button button--primary text-4 mb-4"
					title="Continue to reset password"
					:status="continueStatus"
					type="submit"
					:disabled="validated && invalid || invalidCode"
				>
					Continue
				</c-status-button>
			</ValidationObserver>
		</section>
		<button class="button--no-style text-center block underline w-100" @click="handleResetLogin">
			Return to log in.
		</button>
	</div>
</template>

<script lang="ts">
	import { defineComponent, onMounted, ref } from 'vue';
	import CCallout from '@cognitoforms/shared-components/src/components/content/Callout.vue';
	import CCreatePasswordInput from 'src/components/auth/CreatePasswordInput.vue';
	import type { ButtonStatus } from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CStatusButton from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CButton from '@cognitoforms/shared-components/src/components/interaction/Button.vue';
	import { ValidationObserver } from 'vee-validate';

	export default defineComponent({
		name: 'CPasswordReset',
		components: {
			CCallout,
			CCreatePasswordInput,
			CStatusButton,
			ValidationObserver,
			CButton
		},
		props: {
			code: {
				type: String,
				default: ''
			},
			invalidCode: {
				type: Boolean,
				default: true
			}
		},
		emits: ['reset-password', 'forgot-password', 'reset-login'],
		setup(props, { emit }) {
			const password = ref<string>('');
			const continueStatus = ref<ButtonStatus>('default');
			const observer = ref(null);

			const handleResetPassword = async () => {
				if (await observer.value.validate()) {
					continueStatus.value = 'validating';
					emit('reset-password', password.value);
				}
			};

			onMounted(async () => {
				// Trigger validation so continue is not enabled
				if (observer.value)
					await observer.value.validate();
			});

			const showResetLinkSent = ref<boolean>(false);
			const handleForgotPassword = () => {
				emit('forgot-password');
				showResetLinkSent.value = true;
			};

			const handleResetLogin = () => {
				emit('reset-login');
			};

			return {
				password,
				observer,
				continueStatus,
				showResetLinkSent,
				handleResetPassword,
				handleForgotPassword,
				handleResetLogin
			};
		}
	});
</script>